import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { costContractStatusVariant } from '@/lib/status-variants'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import BillingDifferenceMark from './BillingDifferenceMark'
import { contractViewRows } from '../lib/byContract'
import { describeCostControlError } from '../lib/errors'
import { CONTRACT_STATUS_KEY } from '../lib/labels'
import { formatMoney, isNegativeMoney, isZeroMoney } from '../lib/money'
import type { CostContractView } from '../types'
import ByContractViewSkeleton from './ByContractViewSkeleton'

interface Props { projectId: string; currency: string; locale: string }

const NUM = 'whitespace-nowrap px-2 text-right tabular-nums'

const ByContractView: React.FC<Props> = ({ projectId, currency, locale }) => {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useQuery<{ costContractView: CostContractView }>(Query.COST_CONTRACT_VIEW, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const view = data?.costContractView
  const rows = useMemo(() => (view ? contractViewRows(view, expanded, collapsed) : []), [view, expanded, collapsed])

  if (!view && loading) return <ByContractViewSkeleton />
  if (!view) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-sm text-muted-foreground">
        <p>{describeCostControlError(error?.message, t)}</p>
        <Button variant="outline" size="sm" className="h-8" onClick={() => { refetch().catch(() => undefined) }}>{t('CostControl_retry')}</Button>
      </div>
    )
  }

  const toggle = (set: Set<string>, id: string): Set<string> => { const next = new Set(set); if (!next.delete(id)) next.add(id); return next }
  const money = (v: string) => formatMoney(v, { locale, currency, whole: true })
  const saving = (v: string) => (
    <td className={cn(NUM, !isZeroMoney(v) && (isNegativeMoney(v) ? 'text-destructive' : 'text-emerald-600'))}>{money(v)}</td>
  )
  const investor = !!view.hasInvestorContract
  const billing = (v: { investorBilled: string; billingDifference: string }) => investor && (
    <>
      <td className={NUM}>{money(v.investorBilled)}</td>
      <td className={cn(NUM, isNegativeMoney(v.billingDifference) && 'text-destructive')}>
        <span className="inline-flex items-center justify-end gap-1">
          {isNegativeMoney(v.billingDifference) && <BillingDifferenceMark />}
          {money(v.billingDifference)}
        </span>
      </td>
    </>
  )
  const empty = (n: number) => Array.from({ length: n }, (_, i) => <td key={i} />)

  return (
    <Card className="overflow-x-auto">
      <table className="w-full min-w-[960px] border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-muted text-xs text-muted-foreground">
          <tr className="h-9">
            <th className="px-2 text-left font-medium">{t('CostControl_bycontract_contract')}</th>
            {(['budget', 'committed', 'approved_changes', 'pending_changes', 'certified', 'invoiced', 'saving'] as const).map((k) => (
              <th key={k} className="whitespace-nowrap px-2 text-right font-medium">{t(`CostControl_bycontract_${k}`)}</th>
            ))}
            {investor && (['billed_investor', 'billing_difference'] as const).map((k) => (
              <th key={k} className="whitespace-nowrap px-2 text-right font-medium">{t(`CostControl_bycontract_${k}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            switch (r.kind) {
              case 'contract': return (
                <tr key={r.id} className="h-9 border-t">
                  <td className="px-2">
                    <div className="flex items-center gap-1">
                      {r.expandable ? (
                        <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={t(r.expanded ? 'CostControl_collapse' : 'CostControl_expand')} onClick={() => setExpanded(toggle(expanded, r.id))}>
                          {r.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      ) : <span className="w-6" />}
                      <Link className="font-medium text-primary hover:underline" to={`/project/${projectId}/cost-control/contracts/${r.id}`}>{r.label}</Link>
                      <span className="text-muted-foreground">{r.counterparty}</span>
                      <Badge variant={costContractStatusVariant(r.status)}>{t(CONTRACT_STATUS_KEY[r.status])}</Badge>
                    </div>
                  </td>
                  <td className={NUM}>{money(r.values.budget)}</td>
                  <td className={NUM}>{money(r.values.committed)}</td>
                  <td className={NUM}>{money(r.values.approvedChanges)}</td>
                  <td className={NUM}>{money(r.values.pendingChanges)}</td>
                  <td className={NUM}>{money(r.values.certified)}</td>
                  <td className={NUM}>{money(r.values.invoiced)}</td>
                  {saving(r.values.saving)}
                  {billing(r.values)}
                </tr>
              )
              case 'contractItem': return (
                <tr key={r.id} className="h-8 border-t bg-muted/20">
                  <td className="px-2 pl-12">
                    {r.path && <span className="mr-1 text-muted-foreground">{r.path} /</span>}
                    {r.label.startsWith('CostControl_') ? t(r.label) : r.label}
                  </td>
                  <td className={NUM}>{money(r.values.budget)}</td>
                  <td className={NUM}>{money(r.values.committed)}</td>
                  <td className={NUM}>{money(r.values.approvedChanges)}</td>
                  <td className={NUM}>{money(r.values.pendingChanges)}</td>
                  <td className={NUM}>{money(r.values.certified)}</td>
                  <td className={NUM}>{money(r.values.invoiced)}</td>
                  {saving(r.values.saving)}
                  {billing(r.values)}
                </tr>
              )
              case 'section': return (
                <tr key={`section-${i}`} className="h-9 border-t bg-muted text-xs font-medium text-muted-foreground">
                  <td className="px-2">{t('CostControl_bycontract_uncontracted')}</td>
                  <td className="px-2 text-right">{t('CostControl_bycontract_budget')}</td>
                  <td className="px-2 text-right">{t('CostControl_bycontract_estimate')}</td>
                  <td className="px-2 text-right">{t('CostControl_bycontract_projected')}</td>
                  <td colSpan={3} />
                  <td className="px-2 text-right">{t('CostControl_bycontract_saving')}</td>
                  {investor && <td className="whitespace-nowrap px-2 text-right">{t('CostControl_bycontract_billed_investor')}</td>}
                  {investor && <td />}
                </tr>
              )
              case 'node': return (
                <tr key={r.id} className={cn('h-8 border-t', r.isGroup && 'font-medium')}>
                  <td className="px-2">
                    <div className="flex items-center gap-1">
                      <span className={cn('shrink-0', DEPTH_PAD[Math.min(r.depth, 5)])} />
                      {r.isGroup ? (
                        <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={t(r.collapsed ? 'CostControl_expand' : 'CostControl_collapse')} onClick={() => setCollapsed(toggle(collapsed, r.id))}>
                          {r.collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      ) : <span className="w-6" />}
                      <span>{r.label}</span>
                    </div>
                  </td>
                  <td className={NUM}>{money(r.values.budget)}</td>
                  <td className={NUM}>{money(r.values.estimate)}</td>
                  <td className={NUM}>{money(r.values.projectedFinalCost)}</td>
                  {empty(3)}
                  {saving(r.values.saving)}
                  {investor && <><td className={NUM}>{money(r.values.investorBilled)}</td><td /></>}
                </tr>
              )
              case 'empty': return (
                <tr key={`empty-${r.which}`} className="h-9 border-t">
                  <td colSpan={investor ? 10 : 8} className="px-2 text-muted-foreground">
                    {t(r.which === 'contracts' ? 'CostControl_bycontract_no_contracts' : 'CostControl_bycontract_all_contracted')}
                  </td>
                </tr>
              )
              case 'total': return (
                <tr key="total" className="h-9 border-t-2 bg-muted/40 font-semibold">
                  <td className="px-2">{t('CostControl_bycontract_total')}</td>
                  <td className={NUM}>{money(r.values.budget)}</td>
                  <td className={NUM}>{money(r.values.committed)}</td>
                  <td className={NUM}>{money(r.values.approvedChanges)}</td>
                  <td className={NUM}>{money(r.values.pendingChanges)}</td>
                  <td className={NUM}>{money(r.values.certified)}</td>
                  <td className={NUM}>{money(r.values.invoiced)}</td>
                  {saving(r.values.saving)}
                  {billing(r.values)}
                </tr>
              )
            }
          })}
        </tbody>
      </table>
    </Card>
  )
}

const DEPTH_PAD = ['w-0', 'w-4', 'w-8', 'w-12', 'w-16', 'w-20']

export default ByContractView
